import type { FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
    ArrowRight,
    Check,
    CircleAlert,
    Eye,
    EyeOff,
    LoaderCircle,
    LockKeyhole,
    Smile,
    UserRound
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiRequest } from '@/lib/client-api'
import { useFrame } from '@/components/app/app-frame'
import { AuthField } from '@/components/login/auth-field'

type Mode = 'login' | 'register'

type Field = 'name' | 'username' | 'password'

type Values = Record<Field, string>

type Errors = Partial<Record<Field, string>>

const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,30}$/

const COPY: Record<Mode, { title: string, subtitle: string, submit: string, busy: string, switchPrompt: string, switchAction: string }> = {
    login: {
        title: 'Accedi',
        subtitle: 'Entra con nome utente e password e guarda cosa è arrivato oggi.',
        submit: 'Accedi',
        busy: 'Accesso in corso',
        switchPrompt: 'Non hai ancora un account?',
        switchAction: 'Creane uno'
    },
    register: {
        title: 'Crea il tuo account',
        subtitle: 'Bastano tre campi. Poi le aziende giuste le cerchiamo noi.',
        submit: 'Crea account',
        busy: 'Creazione in corso',
        switchPrompt: 'Hai già un account?',
        switchAction: 'Accedi'
    }
}

const validate = (mode: Mode, values: Values): Errors => {
    const errors: Errors = {}

    if (mode === 'register' && !values.name.trim()) errors.name = 'Scrivi come ti chiami.'

    if (!values.username.trim()) errors.username = 'Inserisci il nome utente.'
    else if (mode === 'register' && !USERNAME_PATTERN.test(values.username.trim())) errors.username = 'Da 3 a 30 caratteri: lettere, numeri, punto, trattino o underscore.'

    if (!values.password) errors.password = 'Inserisci la password.'
    else if (mode === 'register' && values.password.length < 8) errors.password = 'Usa almeno 8 caratteri.'

    return errors
}

const wait = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms))

export const AuthForm: FC = (): ReactNode => {
    const router = useRouter()
    const { cover } = useFrame()
    const [mode, setMode] = useState<Mode>('login')
    const [values, setValues] = useState<Values>({ name: '', username: '', password: '' })
    const [errors, setErrors] = useState<Errors>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle')
    const [showPassword, setShowPassword] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const nameRef = useRef<HTMLInputElement>(null)
    const usernameRef = useRef<HTMLInputElement>(null)
    const passwordRef = useRef<HTMLInputElement>(null)
    const copy = COPY[mode]
    const busy = status !== 'idle'

    const shake = () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        formRef.current?.animate([
            { transform: 'translateX(0)' },
            { transform: 'translateX(-9px)' },
            { transform: 'translateX(8px)' },
            { transform: 'translateX(-6px)' },
            { transform: 'translateX(4px)' },
            { transform: 'translateX(0)' }
        ], { duration: 420, easing: 'cubic-bezier(0.36, 0.07, 0.19, 0.97)' })
    }

    const update = (field: Field, value: string) => {
        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
        if (formError) setFormError(null)
    }

    const switchMode = () => {
        setMode(current => current === 'login' ? 'register' : 'login')
        setErrors({})
        setFormError(null)
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (busy) return

        const nextErrors = validate(mode, values)

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length > 0) {
            shake()

            if (nextErrors.name) nameRef.current?.focus()
            else if (nextErrors.username) usernameRef.current?.focus()
            else passwordRef.current?.focus()

            return
        }

        setStatus('loading')
        setFormError(null)

        try {
            if (mode === 'login') {
                await apiRequest('/api/auth/login', 'POST', { username: values.username.trim(), password: values.password })
            }
            else {
                await apiRequest('/api/auth/register', 'POST', { name: values.name.trim(), username: values.username.trim(), password: values.password })
            }
        }
        catch (error) {
            setStatus('idle')
            setFormError(error instanceof Error ? error.message : 'Qualcosa non ha funzionato. Riprova.')
            shake()

            return
        }

        setStatus('success')
        await wait(380)
        await cover(buttonRef.current)
        await router.push('/')

        // The session cookie was refused (for example by a browser that blocks cookies in embedded pages)
        if (window.location.pathname === '/accedi') {
            setStatus('idle')
            setFormError('Il browser sta bloccando i cookie di accesso. Consentili per questo sito e riprova.')
        }
    }

    return (
        <div className='px-6 pt-8 pb-[max(2rem,env(safe-area-inset-bottom))]'>
            <div key={mode} className='animate-in duration-500 fade-in fill-mode-both slide-in-from-bottom-3 delay-100'>
                <h1 className='font-heading text-[34px] font-semibold leading-[1.05] tracking-tight text-ink'>
                    {copy.title}
                </h1>

                <p className='mt-2.5 text-[15px] leading-relaxed text-muted-foreground'>
                    {copy.subtitle}
                </p>
            </div>

            <form
                ref={formRef}
                noValidate
                onSubmit={handleSubmit}
                aria-describedby={formError ? 'auth-form-error' : undefined}
                className='mt-7'
            >
                <div
                    className={cn(
                        'grid transition-[grid-template-rows,opacity,margin] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
                        mode === 'register' ? 'mb-3 grid-rows-[1fr] opacity-100' : 'mb-0 grid-rows-[0fr] opacity-0'
                    )}
                    inert={mode !== 'register'}
                >
                    <div className='min-h-0 overflow-hidden p-1'>
                        <AuthField
                            ref={nameRef}
                            id='auth-name'
                            label='Come ti chiami'
                            icon={Smile}
                            autoComplete='name'
                            value={values.name}
                            error={errors.name}
                            onChange={event => update('name', event.target.value)}
                        />
                    </div>
                </div>

                <div className='flex animate-in flex-col gap-3 px-1 duration-500 fade-in fill-mode-both slide-in-from-bottom-4 delay-200'>
                    <AuthField
                        ref={usernameRef}
                        id='auth-username'
                        label='Nome utente'
                        icon={UserRound}
                        autoComplete='username'
                        autoCapitalize='none'
                        autoCorrect='off'
                        spellCheck={false}
                        value={values.username}
                        error={errors.username}
                        hint={mode === 'register' ? 'Lettere, numeri, punto o trattino.' : undefined}
                        onChange={event => update('username', event.target.value)}
                    />

                    <AuthField
                        ref={passwordRef}
                        id='auth-password'
                        label='Password'
                        icon={LockKeyhole}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                        value={values.password}
                        error={errors.password}
                        hint={mode === 'register' ? 'Almeno 8 caratteri.' : undefined}
                        onChange={event => update('password', event.target.value)}
                        trailing={(
                            <button
                                type='button'
                                aria-pressed={showPassword}
                                onClick={() => setShowPassword(current => !current)}
                                className='flex size-11 items-center justify-center rounded-full text-ink/55 transition-colors hover:bg-secondary hover:text-ink focus-visible:outline-2 focus-visible:outline-brand'
                            >
                                {showPassword ? <EyeOff aria-hidden='true' className='size-5' /> : <Eye aria-hidden='true' className='size-5' />}
                                <span className='sr-only'>{showPassword ? 'Nascondi password' : 'Mostra password'}</span>
                            </button>
                        )}
                    />
                </div>

                <div aria-live='assertive' className='px-1'>
                    {formError && (
                        <p
                            id='auth-form-error'
                            className='mt-4 flex animate-in items-start gap-2.5 rounded-[16px] bg-[#fde6df] px-4 py-3 text-[14px] font-medium leading-snug text-destructive duration-300 fade-in slide-in-from-top-2'
                        >
                            <CircleAlert aria-hidden='true' className='mt-px size-[18px] shrink-0' />
                            {formError}
                        </p>
                    )}
                </div>

                <div className='mt-6 animate-in px-1 duration-500 fade-in fill-mode-both slide-in-from-bottom-4 delay-300'>
                    <button
                        ref={buttonRef}
                        type='submit'
                        aria-disabled={busy}
                        className={cn(
                            'group relative flex h-[60px] w-full items-center justify-center overflow-hidden rounded-full bg-ink text-[16px] font-semibold text-white shadow-[0_18px_34px_-16px_rgba(26,22,19,0.75)] transition-[transform,background-color] duration-300 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand',
                            !busy && 'hover:bg-ink-soft active:scale-[0.98]',
                            status === 'success' && 'bg-success'
                        )}
                    >
                        {status === 'idle' && (
                            <>
                                <span>{copy.submit}</span>

                                <span aria-hidden='true' className='absolute top-1/2 right-2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-brand text-ink transition-transform duration-300 group-hover:translate-x-0.5'>
                                    <ArrowRight className='size-5' />
                                </span>
                            </>
                        )}

                        {status === 'loading' && (
                            <span className='flex items-center gap-2.5'>
                                <LoaderCircle aria-hidden='true' className='size-5 animate-spin' />
                                {copy.busy}
                            </span>
                        )}

                        {status === 'success' && (
                            <span className='flex animate-pop items-center gap-2'>
                                <Check aria-hidden='true' className='size-5' strokeWidth={3} />
                                Fatto
                            </span>
                        )}
                    </button>
                </div>
            </form>

            <p className='mt-6 animate-in text-center text-[14px] text-muted-foreground duration-500 fade-in fill-mode-both delay-500'>
                {copy.switchPrompt}{' '}
                <button
                    type='button'
                    onClick={switchMode}
                    className='rounded-md font-semibold text-brand-ink underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
                >
                    {copy.switchAction}
                </button>
            </p>
        </div>
    )
}
